import {
  Box,
  FlexBox,
  Icon,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';

import { KeyboardKeyCombinations } from 'components/keyboard';
import { EnrichedCommand } from 'utils/commands';

import styles from './keybindingCommand.module.scss';

type KeybindingCommandProps = {
  /** The command to render keybindings of. */
  enrichedCommand: EnrichedCommand;
};

/** Represents a component for displaying a command and its keybinding. */
export const KeybindingCommand = (
  props: KeybindingCommandProps,
): JSX.Element => {
  const { enrichedCommand } = props;
  const { command, hasKeybindings, isUserDefinedKeybindings, conflicts } =
    enrichedCommand;
  const { name, label, keyboard } = command;
  const hasConflicts = !!conflicts.length && !isUserDefinedKeybindings;
  const keybindings = keyboard?.keybindings ?? [];
  const conflictTooltipContent = (
    <FlexBox gap="200" flexDirection="column">
      <Box>
        Keybindings for this command are not available until the conflicts are
        resolved.
      </Box>

      <Box>Conflicting commands:</Box>

      <Box>
        {conflicts.map((conflict) => (
          <Box key={conflict.command.name}>
            <strong>{conflict.command.label}</strong>
          </Box>
        ))}
      </Box>
    </FlexBox>
  );

  const prettyKeybindings = hasKeybindings ? (
    <FlexBox alignItems="center" gap="300">
      {hasConflicts && (
        <Tooltip
          className={styles.conflictTooltip}
          content={conflictTooltipContent}
        >
          <Icon
            className={styles.conflictIcon}
            name="exclamation-triangle-filled"
          />
        </Tooltip>
      )}
      <KeyboardKeyCombinations keyCombinations={keybindings} variant="rich" />
    </FlexBox>
  ) : (
    <Text.Paragraph className={styles.unassigned} as="em" subdued>
      Unassigned
    </Text.Paragraph>
  );

  return (
    <Table.Tr>
      <Table.Td>
        <FlexBox
          key={name}
          gap="200"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text.Paragraph variant="2">{label}</Text.Paragraph>

          {prettyKeybindings}
        </FlexBox>
      </Table.Td>
    </Table.Tr>
  );
};
